/**
 * AdminSecurityPage — Security settings, login activity, and access controls.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Monitor, Smartphone, ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { ModuleCard, ModuleLoadingState } from '../../shared/components/ModulePageHeader';

interface LoginEvent {
  id: string;
  user_email: string | null;
  ip_address: string | null;
  device: string | null;
  success: boolean;
  created_at: string;
}

const DeviceIcon: React.FC<{ device?: string | null }> = ({ device }) => {
  const isMobile = String(device ?? '').toLowerCase().includes('mobile');
  return isMobile ? <Smartphone size={12} /> : <Monitor size={12} />;
};

export const AdminSecurityPage: React.FC = () => {
  const { workspace } = useWorkspace();
  const orgId = workspace?.organizationId ?? '';
  const [showOnlyFailed, setShowOnlyFailed] = useState(false);

  const logsQuery = useQuery({
    queryKey: ['admin-security-logs', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<LoginEvent[]> => {
      // Try audit_logs first, fallback to empty
      try {
        const { data, error } = await supabase
          .from('audit_logs')
          .select('id, user_id, action, metadata, created_at')
          .eq('organization_id', orgId)
          .like('action', '%login%')
          .order('created_at', { ascending: false })
          .limit(100);

        if (error) return [];

        return (data || []).map((row: any) => ({
          id: row.id,
          user_email: row.metadata?.email ?? null,
          ip_address: row.metadata?.ip_address ?? row.metadata?.ip ?? null,
          device: row.metadata?.device ?? null,
          success: row.action === 'login_success',
          created_at: row.created_at,
        }));
      } catch {
        return [];
      }
    },
  });

  const logs = logsQuery.data ?? [];
  const filtered = showOnlyFailed ? logs.filter((l) => !l.success) : logs;

  type SortKey = 'user' | 'ip' | 'device' | 'status' | 'date';
  type SortDir = 'asc' | 'desc';
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const handleSortChange = useCallback((key: SortKey) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir(key === 'date' ? 'desc' : 'asc');
      return key;
    });
  }, []);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let av: string | number = '';
      let bv: string | number = '';
      switch (sortKey) {
        case 'user':
          av = (a.user_email ?? '').toLowerCase();
          bv = (b.user_email ?? '').toLowerCase();
          break;
        case 'ip':
          av = (a.ip_address ?? '').toLowerCase();
          bv = (b.ip_address ?? '').toLowerCase();
          break;
        case 'device':
          av = (a.device ?? '').toLowerCase();
          bv = (b.device ?? '').toLowerCase();
          break;
        case 'status':
          av = a.success ? 1 : 0;
          bv = b.success ? 1 : 0;
          break;
        case 'date':
          av = new Date(a.created_at).getTime();
          bv = new Date(b.created_at).getTime();
          break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const loginColumns: ReadonlyArray<{ key: SortKey; label: string }> = [
    { key: 'user', label: 'Usuário' },
    { key: 'ip', label: 'IP' },
    { key: 'device', label: 'Dispositivo' },
    { key: 'status', label: 'Status' },
    { key: 'date', label: 'Data/Hora' },
  ];

  return (
    <RequireCapability capability="admin.access" realRoleOnly>
      <AdminConsoleLayout>
        <div className="enterprise-page h-full overflow-y-auto">

          {/* Security Settings */}
          <ModuleCard title="Configurações de Segurança">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SettingRow
                title="Autenticação em 2 Fatores (2FA)"
                description="Exigir 2FA para todos os usuários admin"
                value="Recomendado"
                badgeColor="warning"
              />
              <SettingRow
                title="Expiração de Sessão"
                description="Tempo máximo de sessão inativa"
                value="24 horas"
                badgeColor="neutral"
              />
              <SettingRow
                title="Política de Senhas"
                description="Comprimento mínimo e complexidade"
                value="8 caracteres"
                badgeColor="neutral"
              />
              <SettingRow
                title="IP Allowlist"
                description="Restringir acesso por endereço IP"
                value="Não configurado"
                badgeColor="warning"
              />
            </div>
          </ModuleCard>

          {/* Login Activity */}
          <ModuleCard
            title="Atividade de Login"
            action={
              <label className="flex items-center gap-2 text-[11px] enterprise-text-secondary cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded"
                  checked={showOnlyFailed}
                  onChange={(e) => setShowOnlyFailed(e.target.checked)}
                />
                Apenas falhas
              </label>
            }
          >
            {logsQuery.isPending && <ModuleLoadingState label="Carregando logs" />}

            {!logsQuery.isPending && !filtered.length && (
              <p className="text-[12px] enterprise-text-secondary py-4 text-center">
                {logs.length === 0
                  ? 'Nenhum evento de login registrado. Verifique se a tabela audit_logs está configurada.'
                  : 'Nenhuma falha de login registrada.'}
              </p>
            )}

            {!logsQuery.isPending && filtered.length > 0 && (
              <div className="enterprise-table-shell">
                <table className="w-full text-left text-sm">
                  <thead className="enterprise-table-header">
                    <tr>
                      {loginColumns.map((col) => {
                        const active = sortKey === col.key;
                        const Icon = active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                        return (
                          <th key={col.key} className="enterprise-table-head-cell">
                            <button
                              type="button"
                              onClick={() => handleSortChange(col.key)}
                              className="inline-flex items-center gap-1 text-left hover:text-[var(--text-primary)]"
                            >
                              <span>{col.label}</span>
                              <Icon size={10} className={active ? '' : 'opacity-40'} />
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((log) => (
                      <tr key={log.id} className="enterprise-table-row">
                        <td className="enterprise-table-cell font-mono text-[12px]">{log.user_email || '—'}</td>
                        <td className="enterprise-table-cell font-mono text-[12px]">{log.ip_address || '—'}</td>
                        <td className="enterprise-table-cell">
                          <span className="flex items-center gap-1 text-[12px] enterprise-text-secondary">
                            <DeviceIcon device={log.device} />
                            {log.device || '—'}
                          </span>
                        </td>
                        <td className="enterprise-table-cell">
                          <span
                            className={`inline-block rounded px-2 py-0.5 text-[11px] font-medium ${
                              log.success
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                                : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                            }`}
                          >
                            {log.success ? 'SUCESSO' : 'FALHA'}
                          </span>
                        </td>
                        <td className="enterprise-table-cell enterprise-text-secondary tabular-nums">
                          {new Date(log.created_at).toLocaleString('pt-BR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ModuleCard>
        </div>
      </AdminConsoleLayout>
    </RequireCapability>
  );
};

const SettingRow: React.FC<{
  title: string;
  description: string;
  value: string;
  badgeColor: 'ok' | 'warning' | 'error' | 'neutral';
}> = ({ title, description, value, badgeColor }) => {
  const colors: Record<string, string> = {
    ok: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    error: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    neutral: 'bg-slate-100 text-slate-600 dark:bg-zinc-800 dark:text-zinc-400',
  };
  return (
    <div className="flex items-start justify-between gap-3 p-3 rounded border border-enterprise-border-soft">
      <div className="min-w-0">
        <p className="text-[12px] font-medium enterprise-text-primary">{title}</p>
        <p className="text-[11px] enterprise-text-secondary mt-0.5">{description}</p>
      </div>
      <span className={`shrink-0 text-[11px] font-medium rounded px-2 py-0.5 ${colors[badgeColor]}`}>
        {value}
      </span>
    </div>
  );
};
